import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { LongLogoBlack } from '@images';

import { Icon, SidebarItem } from '@components';
import { FEATURE_FLAGS } from '@constants/feature';
import { AccountType, OrgStatus } from '@constants/generic';
import {
  DASHBOARD,
  GROUPS,
  LEAD_MANAGEMENT,
  PAGE_NOT_FOUND
} from '@constants/routes';
import {
  INDIVIDUAL_MENU_ITEMS,
  INDIVIDUAL_PROFILE_ITEMS,
  ORG_MENU_ITEMS,
  ORG_PROFILE_SECTIONS,
  PROFILE_ITEM_PATHS
} from '@constants/sideMenu';
import { canAccessBranchQuickCollect } from '@features/quick-collect/helpers/quickCollectPermissions';
import {
  hasAnyRoleScope,
  isSalesAgent as isSalesAgentScope,
  LEAD_MANAGEMENT_ROLES
} from '@helpers/roleScope';
import useCheckPermission from '@hooks/checkPermission';
import BranchSelection from '@layouts/main-header/components/BranchSelection';
import CreateBranchModal from '@layouts/main-header/components/CreateBranchModal';
import useOnClickOutside from '@utils/hooks';
import { Org } from 'types/organization';

import AccountModals from './AccountModals';
import useBranchSelection from './hooks/useBranchSelection';
import OrgDetails from './OrgDetails';
import ProfileOptions from './ProfileOptions';

/** Nested lead-management items sales agents must not see. */
const SALES_AGENT_HIDDEN_NAV_IDS = new Set(['agents']);

const TRIAL_ORG_ALLOWED_IDS = new Set([
  'org-dashboard',
  'report',
  'expence-tracker',
  'lead-management',
  'team-management',
  'subscriptions',
  'org-settings'
]);
const TRIAL_INDIVIDUAL_ALLOWED_IDS = new Set([
  'report',
  'org-settings',
  'subscriptions'
]);

interface SideNavBarProps {
  orgList: Org[];
  subscriptionData: any;
}

const SideNavBar = (props: SideNavBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isProfileList = PROFILE_ITEM_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );

  const { orgList, subscriptionData } = props;

  const ref = useRef(null);
  const { currentOrg, accountType } = useSelector((state: any) => state.user);
  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  const { currentBranchFilter } = useSelector((state: any) => state.branch);
  const isTrialMode = useSelector((state: any) => state.trial?.isTrialMode);

  const [accountMethods, setAccountMethods] = useState<any>(null);

  useOnClickOutside(ref, (e) => {
    e.stopPropagation();
  });

  const { checkPermission } = useCheckPermission();
  const isSalesAgent = isSalesAgentScope(rolesAndPermissions);

  const filterNavChildren = (navItem: any) => {
    if (!navItem?.children?.length) return navItem;

    return {
      ...navItem,
      children: navItem.children.filter((child: any) => {
        if (isSalesAgent && SALES_AGENT_HIDDEN_NAV_IDS.has(child.id)) {
          return false;
        }
        if (
          child.permissions &&
          !child.permissions.some((permission: string) =>
            checkPermission([permission])
          )
        ) {
          return false;
        }
        return true;
      })
    };
  };

  const {
    showBranchView,
    createBranchModalOpen,
    selectedBranch,
    handleBranchChange,
    handleAddNewBranch,
    setCreateBranchModalOpen,
    formData,
    setFormData,
    onChangeBranchName,
    onChangeCurrency,
    onBlurBranchName,
    onClickSave,
    error,
    branchCount
  } = useBranchSelection({ subscriptionData });

  const showSideNavBarBackButton = !hasAnyRoleScope(
    currentOrg,
    LEAD_MANAGEMENT_ROLES
  );

  const isOrg = accountType.type === AccountType.ORGANIZATION;

  const menuItems = isOrg ? ORG_MENU_ITEMS : INDIVIDUAL_MENU_ITEMS;

  const hasNavItemPermission = (navItem: {
    id?: string;
    permissions?: string[];
  }) => {
    if (navItem.id === 'quick-collect') {
      return canAccessBranchQuickCollect(rolesAndPermissions?.permissions);
    }
    if (!navItem?.permissions) return true;
    return navItem.permissions.some((permission) =>
      checkPermission([permission])
    );
  };

  const permittedSideNavItems = (menuItems as any)
    ?.filter(
      (navItem: any) =>
        hasNavItemPermission(navItem) &&
        (!navItem?.featureFlag ||
          orgFeatureFlags?.includes(navItem?.featureFlag))
    )
    ?.map(filterNavChildren);

  const isExcludedSideNavItem = (id: string) => {
    if (id === 'subscriptions')
      return (
        currentOrg?.status === OrgStatus.VERIFICATION_PENDING ||
        currentOrg?.status === OrgStatus.VERIFICATION_SUBMITTED
      );

    if (id === 'event-registration')
      return !(
        orgFeatureFlags && orgFeatureFlags?.includes(FEATURE_FLAGS.EVENTS)
      );

    if (id === 'docs')
      return !(
        orgFeatureFlags &&
        orgFeatureFlags.includes(FEATURE_FLAGS.API_INTEGRATIONS)
      );

    if (id === 'org-dashboard') return !!currentOrg?.has_multiple_currencies;

    return false;
  };

  // Helper to filter and exclude items in a section
  const getPermittedSectionItems = (section: any[]) =>
    section
      .filter(
        (navItem: any) =>
          hasNavItemPermission(navItem) &&
          // eslint-disable-next-line max-len
          (!navItem?.featureFlag ||
            orgFeatureFlags?.includes(navItem?.featureFlag)) &&
          !isExcludedSideNavItem(navItem.id)
      )
      .map(filterNavChildren);

  const fullProfileSections = isOrg
    ? ORG_PROFILE_SECTIONS
    : INDIVIDUAL_PROFILE_ITEMS;
  const trialAllowedIds = isOrg
    ? TRIAL_ORG_ALLOWED_IDS
    : TRIAL_INDIVIDUAL_ALLOWED_IDS;

  const profileSections = isTrialMode
    ? (fullProfileSections as any[][])
        .map((section) =>
          section.filter((item: any) => trialAllowedIds.has(item.id))
        )
        .filter((section) => section.length > 0)
    : fullProfileSections;

  const renderProfileListWithSections = () =>
    profileSections.map((section) => {
      const permittedItems = getPermittedSectionItems(section);
      if (permittedItems.length === 0) return null;

      const sectionKey =
        permittedItems[0]?.id ||
        JSON.stringify(permittedItems.map((i) => i.id));
      const sectionIdx = profileSections.indexOf(section);

      let showDivider = false;
      if (sectionIdx < profileSections.length - 1) {
        const nextSection = profileSections[sectionIdx + 1];
        const nextPermitted = getPermittedSectionItems(nextSection);
        showDivider = nextPermitted.length > 0;
      }

      return (
        <div key={sectionKey}>
          {permittedItems.map((item) => (
            <SidebarItem
              item={item}
              path={item.route}
              isOrgLevelItem
              key={item.id}
              isProfileList={isProfileList}
            />
          ))}
          {showDivider && <div className="my-2 h-px w-full bg-cofeeNeutral5" />}
        </div>
      );
    });

  const backNavigation = () => {
    navigate(DASHBOARD);
  };

  const renderSidebarElement = () =>
    permittedSideNavItems.map((item: any) => {
      if (isExcludedSideNavItem(item.id)) return undefined;
      const { id, route } = item;
      let path = route;

      if (id === 'groups') {
        path = GROUPS.HOME;
      }

      return (
        <SidebarItem
          item={item}
          path={path}
          key={id}
          branchId={currentBranchFilter?.branchId}
          isProfileList={isProfileList}
        />
      );
    });

  const isSandBoxEnv = process.env.REACT_APP_ENVIRONMENT === 'sandbox';

  // Main menu is separate from profile sections (Lead Management, Docs, etc.).
  // Lead roles often have an empty main menu — only bounce them when they are
  // on a main-nav route they cannot access, not while inside profile routes.
  const isLeadManagementRole = hasAnyRoleScope(
    currentOrg,
    LEAD_MANAGEMENT_ROLES
  );
  const hasMainMenuAccess = permittedSideNavItems.some(
    (item: { id: string }) => !isExcludedSideNavItem(item.id)
  );

  useEffect(() => {
    if (!isLeadManagementRole) return;
    if (location.pathname === PAGE_NOT_FOUND) return;
    if (isProfileList) return;
    if (!hasMainMenuAccess) {
      navigate(LEAD_MANAGEMENT.LEAD_DASHBOARD);
    }
  }, [
    hasMainMenuAccess,
    isLeadManagementRole,
    isProfileList,
    location.pathname,
    navigate
  ]);

  return (
    <>
      {!rolesAndPermissions?.permissions?.length ? (
        <div className="flex h-dvh w-22 flex-col justify-between bg-theme shadow-sm transition-all duration-300 ease-in-out" />
      ) : (
        <div className="no-scrollbar -mt-14.5 flex h-dvh w-55 flex-col justify-between border-r border-r-cofeeNeutral4 bg-theme shadow-sm">
          <div className="flex h-full flex-col justify-between ">
            <div className="flex flex-col">
              <div className="flex h-14.5 items-center border-b border-b-cofeeNeutral4">
                <div className="flex items-center gap-2 px-6 py-4">
                  <LongLogoBlack className="h-8 w-auto" />
                </div>
              </div>
              <div
                className={`no-scrollbar flex max-h-[calc(var(--app-vh)-124px)] flex-col overflow-y-auto py-4 ${
                  isSandBoxEnv ? 'mt-2' : ''
                } ${isProfileList ? '' : 'gap-1'}`}
              >
                {isProfileList ? (
                  <>
                    {showSideNavBarBackButton && (
                      <div className="ml-2">
                        <Icon
                          className="cursor-pointer"
                          name="back"
                          onClick={backNavigation}
                        />
                      </div>
                    )}
                    <div className="mt-2.5">
                      {renderProfileListWithSections()}
                    </div>
                    <ProfileOptions
                      orgList={orgList}
                      accountMethods={accountMethods}
                      subscriptionData={subscriptionData}
                      labelStyle="text-bgGrey"
                      isProfileList={isProfileList}
                    />
                  </>
                ) : (
                  <>
                    {showBranchView && (
                      <>
                        <BranchSelection
                          initialBranchName={selectedBranch}
                          onBranchChange={handleBranchChange}
                          handleAddNewBranch={handleAddNewBranch}
                        />
                        {createBranchModalOpen && (
                          <CreateBranchModal
                            createBranchModalOpen={createBranchModalOpen}
                            setCreateBranchModalOpen={setCreateBranchModalOpen}
                            formData={formData}
                            setFormData={setFormData}
                            onChangeBranchName={onChangeBranchName}
                            onChangeCurrency={onChangeCurrency}
                            onBlurBranchName={onBlurBranchName}
                            onClickSave={onClickSave}
                            error={error}
                            branchCount={branchCount}
                          />
                        )}
                      </>
                    )}
                    {renderSidebarElement()}
                  </>
                )}
              </div>
            </div>
            <OrgDetails />
          </div>
        </div>
      )}
      <AccountModals orgList={orgList} setAccountMethods={setAccountMethods} />
    </>
  );
};

export default SideNavBar;
